import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useMemo } from "react";
import { z } from "zod";
import { queryClient } from "../lib/queryClient";
import { meQuery, type Me } from "../hooks/useAuth";
import { hasPermission } from "../hooks/useCan";
import { DateRangePill } from "../components/reports/DateRangePill";
import { TierYearPill } from "../components/reports/TierYearPill";
import { ReportTabs } from "../components/reports/ReportTabs";
import { ReportTagFilter } from "../components/reports/ReportTagFilter";
import { ReportTagsContext, TAG_FILTER_TABS } from "../components/reports/useReportTags";
import { OverviewTab } from "../components/reports/tabs/OverviewTab";
import { RevenueTab } from "../components/reports/tabs/RevenueTab";
import { SalesByTypeTab } from "../components/reports/tabs/SalesByTypeTab";
import { ClientsTab } from "../components/reports/tabs/ClientsTab";
import { VisitsTab } from "../components/reports/tabs/VisitsTab";
import { TiersTab } from "../components/reports/tabs/TiersTab";
import { RenewalControlTab } from "../components/reports/tabs/RenewalControlTab";
import { RetentionTab } from "../components/reports/tabs/RetentionTab";
import { CrossSellTab } from "../components/reports/tabs/CrossSellTab";
import { SpaTab } from "../components/reports/tabs/SpaTab";
import { OperationsTab } from "../components/reports/tabs/OperationsTab";
import { TrendsTab } from "../components/reports/tabs/TrendsTab";
import { CompareTab } from "../components/reports/tabs/CompareTab";
import { AiReportsTab } from "../components/reports/tabs/AiReportsTab";
import { resolveDateRange, type Preset } from "../components/reports/resolveDateRange";

const compareKind = z.enum(["month", "quarter", "year", "custom"]);

export const searchSchema = z.object({
  tab: z
    .enum([
      "ai",
      "overview",
      "revenue",
      "sales",
      "clients",
      "visits",
      "renewals",
      "tiers",
      "retention",
      "crosssell",
      "spa",
      "operations",
      "trends",
      "compare",
    ])
    .default("overview"),
  preset: z
    .enum(["today", "yesterday", "7d", "30d", "month", "quarter", "ytd", "custom"])
    .default("month"),
  from: z.string().optional(),
  to: z.string().optional(),
  // The service filter takes several ids at once. A bare string is still
  // accepted so links saved while it was single-value keep resolving.
  service_id: z
    .union([z.string(), z.array(z.string())])
    .optional()
    .transform((v) => (v === undefined ? undefined : Array.isArray(v) ? v : [v])),
  discount_plan_id: z.string().optional(),
  // Member tags, shared by every tab that counts people: keep members carrying
  // any `tag`, drop members carrying any `no_tag`. A bare string still parses.
  tag: z
    .union([z.string(), z.array(z.string())])
    .optional()
    .transform((v) => (v === undefined ? undefined : Array.isArray(v) ? v : [v])),
  no_tag: z
    .union([z.string(), z.array(z.string())])
    .optional()
    .transform((v) => (v === undefined ? undefined : Array.isArray(v) ? v : [v])),
  payment_type: z.string().optional(),
  operator: z.string().optional(),
  category: z.string().optional(),
  base_price: z.string().optional(),
  risk_bucket: z.enum(["early", "d30_59", "d60_89", "d90_plus"]).optional(),
  // At-risk card: parent tier label, or "none" for untiered clients.
  risk_tier: z.string().optional(),
  // Tiers tab: calendar-year window (defaults to the current year).
  year: z.coerce.number().int().min(2000).max(2100).optional(),
  // Tiers tab: optional month within the selected year (month-end anchor).
  month: z.coerce.number().int().min(1).max(12).optional(),
  // Tiers tab: first month of a range ending at `month` (Jan when left out).
  month_from: z.coerce.number().int().min(1).max(12).optional(),
  // Tiers tab: "all" = the whole history instead of a year/month.
  span: z.enum(["all"]).optional(),
  compare: z.enum(["prev", "year"]).optional(),
  // Compare tab: independent Period A and Period B selectors.
  a_kind: compareKind.optional(),
  a_anchor: z.string().optional(),
  a_from: z.string().optional(),
  a_to: z.string().optional(),
  b_kind: compareKind.optional(),
  b_anchor: z.string().optional(),
  b_from: z.string().optional(),
  b_to: z.string().optional(),
  // AI tab: a saved chat to open, so a chat opens in a new browser tab.
  chat: z.string().optional(),
});

export const Route = createFileRoute("/_auth/reports")({
  staticData: {
    titleKey: "reports.title",
    breadcrumbKey: "breadcrumb.operate",
    breadcrumbN: 8,
  },
  validateSearch: searchSchema,
  beforeLoad: () => {
    const me = queryClient.getQueryData<Me>(meQuery.queryKey);
    if (!me) return;
    // Gate on the effective RBAC permission (role map + overrides), not the
    // role name — editable role grants must apply here like everywhere else.
    if (!hasPermission(me, "view_reports")) {
      throw redirect({ to: "/clients" });
    }
  },
  component: ReportsScreen,
});

function ReportsScreen() {
  const {
    tab,
    preset,
    from: customFrom,
    to: customTo,
    service_id,
    discount_plan_id,
    payment_type,
    operator,
    category,
    base_price,
    tag,
    no_tag,
    year,
    month,
    month_from,
    span,
    chat,
  } = Route.useSearch();
  const navigate = useNavigate({ from: "/reports" });
  // Stable while the tags stay the same, so the cards' query keys do too.
  const tagKey = JSON.stringify([tag ?? [], no_tag ?? []]);
  const tags = useMemo(() => {
    const [withTags, withoutTags] = JSON.parse(tagKey) as [string[], string[]];
    return {
      tag: withTags.length > 0 ? withTags : undefined,
      no_tag: withoutTags.length > 0 ? withoutTags : undefined,
    };
  }, [tagKey]);
  const range = useMemo(
    () => resolveDateRange(preset as Preset, customFrom, customTo, new Date()),
    [preset, customFrom, customTo],
  );

  return (
    <ReportTagsContext.Provider value={tags}>
      <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 12,
          }}
        >
          {/* No <h1> here — the topbar already shows "Reports". */}
          {TAG_FILTER_TABS.has(tab) && <ReportTagFilter />}
          {/* The Compare tab carries its own Period A / Period B pickers; the
            Tiers tab reports calendar years, so it gets a year pill instead;
            Retention spans all years and needs no range control. */}
          {tab === "tiers" ? (
            <TierYearPill />
          ) : (
            tab !== "compare" && tab !== "retention" && tab !== "ai" && <DateRangePill />
          )}
        </div>

        <ReportTabs />

        <div role="tabpanel">
          {tab === "ai" && (
            <AiReportsTab
              openChatId={chat}
              onChatOpened={() =>
                void navigate({ search: (prev) => ({ ...prev, chat: undefined }), replace: true })
              }
            />
          )}
          {tab === "overview" && <OverviewTab from={range.from} to={range.to} />}
          {tab === "revenue" && (
            <RevenueTab
              from={range.from}
              to={range.to}
              filters={{
                service_id,
                discount_plan_id,
                payment_type,
                operator,
                category,
                base_price,
                tag,
                no_tag,
              }}
            />
          )}
          {tab === "sales" && <SalesByTypeTab from={range.from} to={range.to} />}
          {tab === "clients" && <ClientsTab from={range.from} to={range.to} />}
          {tab === "visits" && <VisitsTab from={range.from} to={range.to} />}
          {tab === "tiers" && (
            <TiersTab
              year={year ?? new Date().getFullYear()}
              month={month}
              monthFrom={month_from}
              all={span === "all"}
            />
          )}
          {tab === "renewals" && <RenewalControlTab from={range.from} to={range.to} />}
          {tab === "retention" && <RetentionTab />}
          {tab === "crosssell" && <CrossSellTab from={range.from} to={range.to} />}
          {tab === "spa" && <SpaTab from={range.from} to={range.to} />}
          {tab === "operations" && <OperationsTab from={range.from} to={range.to} />}
          {tab === "trends" && <TrendsTab from={range.from} to={range.to} />}
          {tab === "compare" && <CompareTab />}
        </div>
      </div>
    </ReportTagsContext.Provider>
  );
}
